import { useState } from 'react';

import { useWriteChannelCredential } from './use-channels';

import type { ChannelConfigWire } from './types';
import type { ReactElement } from 'react';

export interface WriteCredentialDialogProps {
  config: ChannelConfigWire;
  onClose: () => void;
}

/**
 * Gravar ou trocar a credencial do canal.
 *
 * **A consequência é dita ANTES de clicar, não descoberta depois.** Trocar a credencial
 * zera o último teste de conexão no backend (a certeza antiga valia para o segredo
 * antigo). Se a tela não avisasse, a pessoa trocaria o segredo e veria o cartão perder o
 * verde sem entender por quê — e concluiria que quebrou algo.
 *
 * O campo é `password`: o segredo não fica legível no ombro de ninguém, e nunca volta do
 * servidor para ser exibido.
 */
export function WriteCredentialDialog({
  config,
  onClose,
}: WriteCredentialDialogProps): ReactElement {
  const [secret, setSecret] = useState('');
  const gravar = useWriteChannelCredential();

  const nome = config.channel === 'telegram' ? 'bot token' : 'access token';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg border border-border-default bg-surface p-5">
        <h3 className="text-base font-semibold text-text-primary">
          {config.channel === 'telegram' ? 'Credencial do Telegram' : 'Credencial do WhatsApp'}
        </h3>
        <p className="mt-1 text-xs text-text-secondary">
          O segredo é guardado cifrado e nunca mais aparece nesta tela.
        </p>

        <label className="mt-4 block text-xs font-medium text-text-primary" htmlFor="secret">
          {nome.charAt(0).toUpperCase() + nome.slice(1)}
        </label>
        <input
          id="secret"
          type="password"
          autoComplete="off"
          value={secret}
          onChange={(e) => {
            setSecret(e.target.value);
          }}
          className="mt-1 w-full rounded-md border border-border-strong bg-surface px-3 py-2 font-mono text-xs text-text-primary"
        />

        <div className="mt-3 flex gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-700">
          <span className="font-bold">!</span>
          <div>
            Ao gravar, o <strong>último teste de conexão é descartado</strong> — ele valia para o
            segredo anterior. Teste de novo logo em seguida para saber se o novo funciona.
          </div>
        </div>

        {gravar.isError && (
          <p className="mt-2 text-xs text-red-700" role="alert">
            {gravar.error.message}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
            disabled={secret.trim() === '' || gravar.isPending}
            onClick={() => {
              gravar.mutate(
                { id: config.id, version: config.version, secret },
                { onSuccess: onClose },
              );
            }}
          >
            {gravar.isPending ? 'Gravando…' : 'Gravar credencial'}
          </button>
          <button
            type="button"
            className="rounded-md border border-border-strong bg-surface px-3 py-1.5 text-xs text-text-secondary"
            onClick={onClose}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
